import { action, observable } from 'mobx';
import { api } from '../config';

// AppStore (used for loading, error throwing, etc.)
import AppStore from './AppStore';

let obx = observable({
  forcePlay: false,
  session: null,
  sessions: [],
  sessionData: {},
  shares: [],
  hideInfoFlags: true,
  shareSid: null,
  password: null,

  /**
   * getSession - Gets all session details including videos
   * @param {string} installSid
   * @param {string} examSid
   * @param {string} sessionSid
   */
  getSession: action((installSid, examSid, sessionSid) => {
    AppStore.startLoading('getSession');

    api.get(`/installs/${installSid}/exams/${examSid}/sessions/${sessionSid}`)
      .then(response => {
        // Add installSid and examSid to annotations
        if (response.data.automated.annotations) {
          response.data.automated.annotations.forEach(annotation => {
            annotation.installSid = installSid;
            annotation.examSid = examSid;
          })
        }

        obx.sessions.push(response.data);

        // If the user is not viewing a shared video,
        // get a list of all shares.
        if (!AppStore.sharedViewer) {
          const urlData = {
            examSid,
            installSid,
            sessionSid
          };

          obx.getSessionShares(urlData);
        }

        AppStore.finishLoading('getSession');
      })
      .catch(error => {
        AppStore.throwError(error);
      })
  }),

  /**
   * checkFlag - Displays a toast message asking to confirm or deny a flag
   * @param {object} flag
   * @param {function} callback
   */
  checkFlag: action((flag, callback) => {
    const settings = {
      content: flag.title,
      type: 'warn',
      dismissible: false,
      actions: [
        {
          buttonText: AppStore.translation.Player.check_flag.confirm,
          action: () => { obx.confirmFlag(flag); callback && callback(); },
          type: 'positive'
        }, {
          buttonText: AppStore.translation.Player.check_flag.deny,
          action: () => { obx.denyFlag(flag); callback && callback(); },
          type: 'negative'
        }
      ]
    };

    AppStore.showToast(settings);
  }),

  /**
   * confirmFlag
   * @param {object} flag
   */
  confirmFlag: action((flag) => {
    flag.status = 'reviewed';
    obx.forcePlay = true;

    const payload = {
      status: 'reviewed'
    };

    api.put(`/installs/${flag.installSid}/exams/${flag.examSid}/sessions/${flag.examSessionSid}/annotations/${flag.sid}`, payload)
      .catch(error => {
        AppStore.throwError(error);
      })
  }),

  /**
   * denyFlag
   * @param {object} flag
   */
  denyFlag: action((flag) => {
    flag.status = 'denied';
    obx.forcePlay = true;

    api.delete(`/installs/${flag.installSid}/exams/${flag.examSid}/sessions/${flag.examSessionSid}/annotations/${flag.sid}`)
      .catch(error => {
        AppStore.throwError(error);
      })
  }),

  /**
   * Share Playback URL with Another User
   * 
   * @function sharePlayback
   * @param {object} urlData
   * @param {function} callback
   */
  sharePlayback: (urlData, callback) => {
    const { examSid, installSid, sessionSid, userSid } = urlData;

    AppStore.startLoading('sharePlayback');

    const url = `/installs/${installSid}/exams/${examSid}/sessions/${sessionSid}/user/${userSid}/share`;

    api.put(url)
      .then(response => {
        obx.shareSid = response.data.shareSid;
        obx.sharePassword = response.data.password;

        if (callback) {
          callback();
        }

        // Make sure we have an up-to-date list of shares
        obx.getSessionShares({ examSid, installSid, sessionSid });

        AppStore.finishLoading('sharePlayback');
      })
      .catch(error => {
        AppStore.throwError(error);
      });
  },

  /**
   * Get all session shares
   * 
   * @function getSessionShares
   * @param {object} urlData
   */
  getSessionShares: urlData => {
    const { examSid, installSid, sessionSid } = urlData;

    AppStore.startLoading('getSessionShares');

    const url = `/installs/${installSid}/exams/${examSid}/session/${sessionSid}/shareRecords`;

    api.get(url)
      .then(response => {
        obx.shares = response.data;

        AppStore.finishLoading('getSessionShares');
      })
      .catch(error => {
        AppStore.throwError(error);
      });
  },

  /**
   * Delete session share
   * 
   * @function deleteSessionShare
   * @param {object} urlData
   * @param {function} callback
   */
  deleteSessionShare: (urlData, callback) => {
    const { examSid, installSid, sessionSid, shareSid } = urlData;

    const url = `/installs/${installSid}/exams/${examSid}/session/${sessionSid}/shareRecords/${shareSid}`;

    api.delete(url)
      .then(() => {
        // Remove the deleted share from the stored array
        obx.shares.some((share, i) => {
          if (share.shareSid === shareSid) {
            action(() => {
              obx.shares.splice(i, 1);
            })();
    
            return true;
          }

          return false;
        });

        if (callback) {
          callback();
        }

        AppStore.finishLoading('sharePlayback');
      })
      .catch(error => {
        AppStore.throwError(error);
      });
  }
});

export default obx;